import React, { useState } from 'react';
import { Scissors, Square, Hand } from 'lucide-react';

interface RPSGameProps {
  onComplete: (score: number) => void;
}

const CHOICES = [
    { id: 'rock', label: 'אבן', icon: Square },
    { id: 'paper', label: 'נייר', icon: Hand },
    { id: 'scissors', label: 'מספריים', icon: Scissors },
];

export default function RockPaperScissorsGame({ onComplete }: RPSGameProps) {
  const [playerChoice, setPlayerChoice] = useState<string | null>(null);
  const [computerChoice, setComputerChoice] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);

  const playGame = (choiceId: string) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setPlayerChoice(choiceId);
    setComputerChoice(null);
    setResult(null);

    // Simulate thinking/animation
    setTimeout(() => {
        const randomChoice = CHOICES[Math.floor(Math.random() * CHOICES.length)].id;
        setComputerChoice(randomChoice);
        
        let gameResult = 'DRAW';
        if (choiceId === randomChoice) {
            gameResult = 'DRAW';
        } else if (
            (choiceId === 'rock' && randomChoice === 'scissors') ||
            (choiceId === 'paper' && randomChoice === 'rock') ||
            (choiceId === 'scissors' && randomChoice === 'paper')
        ) {
            gameResult = 'WIN';
            setScore(s => s + 5);
        } else {
            gameResult = 'LOSE';
        }
        
        setResult(gameResult);
        setIsAnimating(false);
        setRound(r => r + 1);

        if (round >= 5) {
            setTimeout(() => {
                onComplete(score + (gameResult === 'WIN' ? 5 : 0));
            }, 2000);
        }
    }, 1000);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-4 text-foreground">
        <h2 className="text-2xl font-bold mb-4">אבן נייר ומספריים</h2>
        <div className="flex justify-between w-full max-w-xs mb-8 text-muted-foreground">
            <span>סיבוב: {round > 5 ? 5 : round}/5</span>
            <span>ניקוד: {score}</span>
        </div>

        <div className="flex gap-8 items-center justify-center mb-12 h-32">
            {/* Player */}
            <div className={`transition-all duration-500 ${isAnimating ? 'animate-bounce' : ''}`}>
                 {playerChoice ? (
                     <div className="flex flex-col items-center">
                         {React.createElement(CHOICES.find(c => c.id === playerChoice)?.icon || Square, { size: 64, className: 'text-cyan-400' })}
                         <span>אתה</span>
                     </div>
                 ) : (
                     <div className="w-16 h-16 border-2 border-dashed border-border rounded-full flex items-center justify-center text-muted-foreground">?</div>
                 )}
            </div>

            <div className="text-2xl font-bold">VS</div>

            {/* Computer */}
             <div className={`transition-all duration-500 ${isAnimating ? 'animate-bounce' : ''}`}>
                 {computerChoice ? (
                     <div className="flex flex-col items-center">
                         {React.createElement(CHOICES.find(c => c.id === computerChoice)?.icon || Square, { size: 64, className: 'text-rose-400' })}
                         <span>מחשב</span>
                     </div>
                 ) : (
                     <div className="w-16 h-16 border-2 border-dashed border-border rounded-full flex items-center justify-center text-muted-foreground">?</div>
                 )}
            </div>
        </div>

        {result && (
            <div className="mb-8 text-3xl font-bold animate-pulse">
                {result === 'WIN' ? 'ניצחת!' : result === 'LOSE' ? 'הפסדת...' : 'תיקו!'}
            </div>
        )}

        <div className="flex gap-4">
             {CHOICES.map(choice => (
                 <button
                    key={choice.id}
                    onClick={() => playGame(choice.id)}
                    disabled={isAnimating || round > 5}
                    className="flex flex-col items-center gap-2 p-4 bg-muted rounded-xl hover:bg-muted transition-all active:scale-95 disabled:opacity-50"
                 >
                     <choice.icon size={32} />
                     <span>{choice.label}</span>
                 </button>
             ))}
        </div>
    </div>
  );
}
